import type { ReactElement } from 'react';

import { illustrations } from '../../assets/illustrations';
import { SensitivityChip } from '../../atoms/SensitivityChip';
import { MaskIcon } from '../../lib/mask-icon';
import { UserTypeDrillButton, type IdentityKind } from '../../lib/user-type-drill';
import { cn } from '../../lib/utils';
import { IconCount } from '../IconCount';

export interface SensitivityBreakdownRowProps {
  level: string;
  label: string;
  files: number;
  /**
   * Distinct identity counts. Omit them where no relation measures them for
   * this row: the slot stays reserved and prints nothing, because a rendered 0
   * claims nobody touched the files rather than that nobody counted.
   */
  hi?: number;
  nhi?: number;
  /**
   * External-identity count. Always reserves at least a 44px slot per Figma
   * 5:23844, and widens past it for a count like "1,234" rather than spilling;
   * content (icon + number) renders only when `external > 0`.
   */
  external: number;
  /**
   * When supplied, the row renders as <button type="button"> and fires onClick
   * (see `onUserTypeClick` for the exception); when omitted, the row renders as
   * a non-interactive <div>.
   */
  onClick?: () => void;
  /**
   * HI / NHI / External count click (TDEV-1116). When supplied, each rendered
   * count is its own button, so the row can no longer be one: it renders as a
   * <div> that takes `onClick` as a mouse click, and its chip becomes the
   * keyboard <button>.
   */
  onUserTypeClick?: (kind: IdentityKind) => void;
  /** Optional data-testid override (used by SensitivityBody for `sensitivity-row-{id}` handles). */
  testId?: string;
  className?: string;
}

export function SensitivityBreakdownRow({
  level,
  label,
  files,
  hi,
  nhi,
  external,
  onClick,
  onUserTypeClick,
  testId,
  className,
}: SensitivityBreakdownRowProps): ReactElement {
  const chip = <SensitivityChip level={level} label={label} size="lg" />;
  const identityCount = (kind: IdentityKind, count: ReactElement): ReactElement =>
    onUserTypeClick ? (
      <UserTypeDrillButton kind={kind} subject={label} onClick={() => onUserTypeClick(kind)}>
        {count}
      </UserTypeDrillButton>
    ) : (
      count
    );
  const content = (
    <>
      {onClick && onUserTypeClick ? (
        // Activation bubbles to the row's onClick.
        <button
          type="button"
          className="rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-dawn-orange"
        >
          {chip}
        </button>
      ) : (
        chip
      )}
      <IconCount
        className="col-start-3"
        icon={<MaskIcon src={illustrations.fileOutline} size={16} />}
        count={files}
        size={16}
      />
      <span data-testid="sensitivity-breakdown-nhi-slot" className="col-start-5 inline-flex">
        {nhi !== undefined
          ? identityCount(
              'NHI',
              <IconCount
                icon={<MaskIcon src={illustrations.nonHumanIdentityOutline} size={16} />}
                count={nhi}
                size={16}
              />
            )
          : null}
      </span>
      <span data-testid="sensitivity-breakdown-hi-slot" className="col-start-7 inline-flex">
        {hi !== undefined
          ? identityCount(
              'HI',
              <IconCount
                icon={<MaskIcon src={illustrations.humanIdentityOutline} size={16} />}
                count={hi}
                size={16}
              />
            )
          : null}
      </span>
      <span
        data-testid="sensitivity-breakdown-external-slot"
        className={cn(
          'col-start-9 inline-flex min-w-11 shrink-0 items-center gap-1 whitespace-nowrap',
          external > 0 ? 'text-error' : undefined
        )}
      >
        {external > 0
          ? identityCount(
              'External',
              <IconCount
                icon={<MaskIcon src={illustrations.humanIdentityOutline} size={16} />}
                count={external}
                size={16}
              />
            )
          : null}
      </span>
    </>
  );

  // Subgrid row: inherits the 9-column template from SensitivityBody's parent grid
  // (chip | spacer | files | gap | nhi | gap | hi | gap | external).
  // col-span-full + grid-cols-subgrid makes all rows share the parent's column tracks
  // so each column is sized to the widest content across all rows.
  const layoutClass = 'col-span-full grid grid-cols-subgrid items-center justify-items-start';
  const rootTestId = testId ?? 'sensitivity-breakdown-row';

  if (onClick && !onUserTypeClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={rootTestId}
        className={cn(layoutClass, 'cursor-pointer text-left', className)}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      data-testid={rootTestId}
      className={cn(layoutClass, onClick && 'cursor-pointer', className)}
      onClick={onClick}
    >
      {content}
    </div>
  );
}

SensitivityBreakdownRow.displayName = 'SensitivityBreakdownRow';
